import type { DtoArticle, DtoDeliveryServiceResource, DtoProvidedService } from '@repo/api-types/order-retrieval';
import { type OrderAsset } from '@repo/types/order';
import { type TransportTimewindow } from '@repo/types/time-windows';
import { normalizeToUtc } from '@repo/utils';

export const buildCompositeServiceId = (
    serviceItemType: string | undefined,
    serviceItemNo: string | undefined
): string | undefined => (serviceItemType && serviceItemNo ? `${serviceItemType}${serviceItemNo}` : undefined);

export interface MatchedDeliveryService {
    serviceItemNo?: string;
    serviceItemType?: string;
    serviceId?: string;
    deliveryNo?: string;
    serviceSubType?: string;
    referencedItems?: number[];
}

export interface DeliveryServiceWithTimeWindow extends MatchedDeliveryService {
    existingTimeWindow?: {
        timeWindowId: string;
        fromLocalDateTime: string;
        toLocalDateTime: string;
    };
}

export interface MatchedProvidedService {
    serviceItemNo?: string;
    serviceItemType?: string;
    serviceId?: string;
    lineId?: number;
    serviceProviderId?: string;
    serviceProductId?: string;
    capacityUnit?: string;
    capacityValue?: number;
    referencedItems?: number[];
}

interface DeliveryTransportTimeWindowEntry {
    deliveryNo: string;
    referencedItems: number[];
    transportTimewindow: TransportTimewindow;
}

const findDeliveryService = (
    orderData: OrderAsset,
    predicate: (service: DtoDeliveryServiceResource) => boolean
): MatchedDeliveryService | undefined => {
    const deliveryArrangements = orderData.assetDetails._embedded?.deliveryArrangements;
    if (!deliveryArrangements || deliveryArrangements.length === 0) {
        return;
    }

    for (const arrangement of deliveryArrangements) {
        const deliveryServices = arrangement._embedded?.deliveryServices ?? [];

        for (const service of deliveryServices) {
            if (!predicate(service)) {
                continue;
            }

            const serviceSubType =
                typeof arrangement.serviceSubType === 'string' ? arrangement.serviceSubType : undefined;

            return {
                ...(service.serviceItemNo && { serviceItemNo: service.serviceItemNo }),
                ...(service.serviceItemType && { serviceItemType: service.serviceItemType }),
                ...(service.serviceId && { serviceId: service.serviceId }),
                ...(service.deliveryNo && { deliveryNo: service.deliveryNo }),
                ...(serviceSubType && { serviceSubType }),
                ...(service.referencedItems && { referencedItems: service.referencedItems }),
            };
        }
    }

    return;
};

export const findDeliveryServiceByServiceId = (
    orderData: OrderAsset,
    serviceId: string
): MatchedDeliveryService | undefined => {
    return findDeliveryService(orderData, (service) => service.serviceId === serviceId);
};

export const findDeliveryServiceByDeliveryNo = (
    orderData: OrderAsset,
    deliveryNo: string
): MatchedDeliveryService | undefined => {
    return findDeliveryService(orderData, (service) => service.deliveryNo === deliveryNo);
};

export const findAllDeliveryServicesInArrangement = (
    orderData: OrderAsset,
    serviceId: string
): DeliveryServiceWithTimeWindow[] => {
    const deliveryArrangements = orderData.assetDetails._embedded?.deliveryArrangements;
    if (!deliveryArrangements || deliveryArrangements.length === 0) {
        return [];
    }

    for (const arrangement of deliveryArrangements) {
        const deliveryServices = arrangement._embedded?.deliveryServices ?? [];
        const hasMatchingService = deliveryServices.some((service) => service.serviceId === serviceId);

        if (!hasMatchingService) {
            continue;
        }

        const serviceSubType = typeof arrangement.serviceSubType === 'string' ? arrangement.serviceSubType : undefined;

        return deliveryServices.map((service) => {
            const timeWindow = service.timeWindows?.[0];
            const existingTimeWindow =
                timeWindow?.timeWindowId && timeWindow.fromLocalDateTime && timeWindow.toLocalDateTime
                    ? {
                          timeWindowId: timeWindow.timeWindowId,
                          fromLocalDateTime: timeWindow.fromLocalDateTime,
                          toLocalDateTime: timeWindow.toLocalDateTime,
                      }
                    : undefined;

            return {
                ...(service.serviceItemNo && { serviceItemNo: service.serviceItemNo }),
                ...(service.serviceItemType && { serviceItemType: service.serviceItemType }),
                ...(service.serviceId && { serviceId: service.serviceId }),
                ...(service.deliveryNo && { deliveryNo: service.deliveryNo }),
                ...(serviceSubType && { serviceSubType }),
                ...(service.referencedItems && { referencedItems: service.referencedItems }),
                ...(existingTimeWindow && { existingTimeWindow }),
            };
        });
    }

    return [];
};

const extractProvidedServiceFields = (service: DtoProvidedService): MatchedProvidedService => {
    const combinedServiceId = buildCompositeServiceId(service.serviceItemType, service.serviceItemNo);
    const referencedItems = service.referencedItems;

    return {
        ...(service.serviceItemNo && { serviceItemNo: service.serviceItemNo }),
        ...(service.serviceItemType && { serviceItemType: service.serviceItemType }),
        ...(combinedServiceId && { serviceId: combinedServiceId }),
        ...(service.lineId !== undefined && { lineId: service.lineId }),
        ...(service.serviceProviderId && { serviceProviderId: service.serviceProviderId }),
        ...(service.serviceProductId && { serviceProductId: service.serviceProductId }),
        ...(service.capacityUnit && { capacityUnit: service.capacityUnit }),
        ...(service.capacityValue !== undefined && { capacityValue: service.capacityValue }),
        ...(referencedItems && referencedItems.length > 0 && { referencedItems }),
    };
};

export const findProvidedServiceByServiceId = (
    orderData: OrderAsset,
    serviceId: string
): MatchedProvidedService | undefined => {
    const providedServices = orderData.assetDetails._embedded?.providedServices;
    if (!providedServices || providedServices.length === 0) {
        return;
    }

    for (const service of providedServices) {
        const combinedServiceId = buildCompositeServiceId(service.serviceItemType, service.serviceItemNo);

        if (combinedServiceId !== serviceId) {
            continue;
        }

        return extractProvidedServiceFields(service);
    }

    return;
};

const getDeliveryTransportTimeWindowEntries = (
    orderData: OrderAsset,
    deliveryOverride?: { deliveryNo: string; transportTimewindow: TransportTimewindow }
): DeliveryTransportTimeWindowEntry[] => {
    const deliveryArrangements = orderData.assetDetails._embedded?.deliveryArrangements;
    if (!deliveryArrangements || deliveryArrangements.length === 0) {
        return [];
    }

    const transportTimeWindowEntries: DeliveryTransportTimeWindowEntry[] = [];

    for (const arrangement of deliveryArrangements) {
        const deliveryServices = arrangement._embedded?.deliveryServices ?? [];

        for (const deliveryService of deliveryServices) {
            const deliveryNo = deliveryService.deliveryNo;
            if (!deliveryNo) {
                continue;
            }

            if (deliveryNo === deliveryOverride?.deliveryNo) {
                transportTimeWindowEntries.push({
                    deliveryNo,
                    referencedItems: deliveryService.referencedItems ?? [],
                    transportTimewindow: deliveryOverride.transportTimewindow,
                });
                continue;
            }

            const timeWindow = deliveryService.timeWindows?.[0];
            if (
                !timeWindow?.timeWindowId ||
                !timeWindow.fromLocalDateTime ||
                !timeWindow.toLocalDateTime ||
                !timeWindow.timeZone
            ) {
                continue;
            }

            transportTimeWindowEntries.push({
                deliveryNo,
                referencedItems: deliveryService.referencedItems ?? [],
                transportTimewindow: {
                    transportTimewindowId: timeWindow.timeWindowId,
                    start: normalizeToUtc(timeWindow.fromLocalDateTime, timeWindow.timeZone),
                    end: normalizeToUtc(timeWindow.toLocalDateTime, timeWindow.timeZone),
                    serviceIdentifier: deliveryNo,
                },
            });
        }
    }

    return transportTimeWindowEntries;
};

export const filterNonReferenceItems = (referencedItems: number[], articles: DtoArticle[] | undefined): number[] => {
    if (!articles || articles.length === 0) {
        return referencedItems;
    }

    const referenceItemIds = new Set(
        articles
            .filter((article) => article.isReferenceItem)
            .flatMap((article) => [Number(article.externalRefId), article.soSalesItemNo])
    );

    return referencedItems.filter((refId) => !referenceItemIds.has(refId));
};

export const getServiceTransportTimeWindows = (
    orderData: OrderAsset,
    serviceId: string,
    deliveryOverride?: { deliveryNo: string; transportTimewindow: TransportTimewindow }
): TransportTimewindow[] => {
    const matchedProvidedService = findProvidedServiceByServiceId(orderData, serviceId);
    if (!matchedProvidedService?.referencedItems || matchedProvidedService.referencedItems.length === 0) {
        return [];
    }

    const articles = orderData.assetDetails._embedded?.Articles;
    const filteredRefItems = filterNonReferenceItems(matchedProvidedService.referencedItems, articles);
    if (filteredRefItems.length === 0) {
        return [];
    }

    return getDeliveryTransportTimeWindowEntries(orderData, deliveryOverride).flatMap((entry) => {
        const hasOverlap = entry.referencedItems.some((refId) => filteredRefItems.includes(refId));
        return hasOverlap ? [entry.transportTimewindow] : [];
    });
};

export const getAllDeliveryTransportTimeWindows = (
    orderData: OrderAsset,
    deliveryOverride?: { deliveryNo: string; transportTimewindow: TransportTimewindow }
): TransportTimewindow[] => {
    return getDeliveryTransportTimeWindowEntries(orderData, deliveryOverride).map((entry) => entry.transportTimewindow);
};
